import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, AlertTriangle, ShieldCheck, Clock, CreditCard, Sparkles,
} from "lucide-react";
import useRazorpay, { PLATFORM_MERCHANT } from "../../hooks/useRazorpay";
import { getBaseUrl } from "../../utils/getBaseUrl";
import CommonToaster from "../Common/CommonToaster";
import { AppButton } from "../ui";

/**
 * PublicPlanRenewalPage — /pay/plan/:token
 *
 * The page a billing reminder's payment link opens. No login: the token in the
 * URL is the only credential, the same contract the service-invoice and student
 * fee links already use, and the reason those exist — the person who has to act
 * on "your subscription expires in 3 days" is usually reading it on a phone,
 * and "log in first, then find Billing" is where these die.
 *
 * The server owns every number on this page. Prices arrive already discounted
 * and taxed from `GET /public/plan-renewal/:token`; nothing here computes a
 * price, because a page that quotes differently from the checkout is a
 * chargeback rather than a bug report.
 *
 * ⚠️ Tailwind, not the `.pip-*` sheet the other two public payment pages share.
 * A component is one system or the other, never both, and that sheet belongs to
 * those two components — reaching into it from a third is how a half-migrated
 * file becomes a specificity coin-flip.
 */

// Plain axios client — no auth interceptors. The token in the URL is the only
// credential these endpoints need, and an expired LMS session in localStorage
// must not turn a stranger's payment page into a 401 redirect.
const publicApi = axios.create({
  baseURL: getBaseUrl(),
  headers: { "Content-Type": "application/json" },
});

const CYCLES = [
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "yearly", label: "Yearly" },
];

const money = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
  : "—");

/**
 * The page's two shells.
 *
 * Module scope, not inside the component: a component defined in a render body
 * is a NEW type on every render, so React unmounts and remounts its whole
 * subtree — here that would tear down the plan grid on every keystroke and
 * every toast. And components rather than a `const CARD = "tw-…"` string,
 * because the answer to "what is this element" is a name.
 */
const Shell = ({ children }) => (
  <div className="tw-flex tw-min-h-screen tw-justify-center tw-bg-gradient-to-b tw-from-surface-sunken tw-to-surface-hover tw-px-4 tw-pb-16 tw-pt-6">
    {children}
  </div>
);

const PayCard = ({ className = "", children }) => (
  <div
    className={
      "tw-w-full tw-max-w-[960px] tw-rounded-xl tw-bg-surface-raised tw-p-7 tw-text-content "
      + `tw-shadow-[0_4px_24px_var(--card-shadow)] max-[600px]:tw-p-4 ${className}`
    }
  >
    {children}
  </div>
);

const PublicPlanRenewalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [cycle, setCycle] = useState("");
  const [planId, setPlanId] = useState("");
  const [paying, setPaying] = useState(false);
  const [paidInfo, setPaidInfo] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const { openCheckout } = useRazorpay();
  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const fetchLink = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const { data: d } = await publicApi.get(`/public/plan-renewal/${token}`);
      setData(d);
      // Start on what the reminder was about, falling back to the institute's
      // current plan and then the first thing on offer — never an empty state
      // the payer has to resolve themselves.
      const suggestedPlan = d.suggested?.planId
        && d.plans.find((p) => String(p._id) === String(d.suggested.planId));
      const currentPlan = d.plans.find((p) => p.isCurrent);
      const chosen = suggestedPlan || currentPlan || d.plans[0];
      setPlanId(chosen ? String(chosen._id) : "");
      const suggestedCycle = d.suggested?.billingCycle;
      const available = chosen ? CYCLES.filter((c) => chosen.cycles[c.key]) : [];
      setCycle(
        (suggestedCycle && chosen?.cycles[suggestedCycle] && suggestedCycle)
        || available[0]?.key
        || "",
      );
    } catch (err) {
      setLoadError(err.response?.data?.message || "This payment link is not valid.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLink(); }, [fetchLink]);

  const plan = useMemo(
    () => data?.plans.find((p) => String(p._id) === String(planId)) || null,
    [data, planId],
  );
  const priced = plan && cycle ? plan.cycles[cycle] : null;

  // Switching plan can land on a cycle the new plan does not price. Fall back
  // rather than showing an empty amount next to an enabled Pay button.
  useEffect(() => {
    if (!plan) return;
    if (cycle && plan.cycles[cycle]) return;
    const first = CYCLES.find((c) => plan.cycles[c.key]);
    setCycle(first ? first.key : "");
  }, [plan, cycle]);

  const handlePay = async () => {
    if (!plan || !cycle) return;
    setPaying(true);
    try {
      const { data: order } = await publicApi.post(`/public/plan-renewal/${token}/create-order`, {
        planId: plan._id,
        billingCycle: cycle,
      });
      await openCheckout(
        {
          razorpayKeyId: order.keyId,
          order: { id: order.orderId, amount: order.amount, currency: order.currency },
          merchantName: PLATFORM_MERCHANT,
          student: {
            name: order.institute?.name || "",
            email: order.institute?.email || "",
            mobile: order.institute?.phone || "",
          },
          description: `${order.plan?.name || "Plan"} — ${cycle}`,
        },
        async (response) => {
          try {
            const { data: res } = await publicApi.post(`/public/plan-renewal/${token}/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaidInfo(res);
            showToast("Payment successful — your plan is active.");
            fetchLink();
          } catch (err) {
            // The money may well have been taken: Razorpay's webhook activates
            // the plan independently of this callback, so never tell the payer
            // it failed — tell them what actually happened.
            showToast(
              err.response?.data?.message
              || "We could not confirm the payment here. If it was debited it will be applied automatically — please check your email for the receipt.",
              "error",
            );
          } finally {
            setPaying(false);
          }
        },
        (err) => {
          setPaying(false);
          if (err?.reason !== "Payment cancelled by user.") {
            showToast(err?.reason || "Payment failed.", "error");
          }
        },
      );
    } catch (err) {
      setPaying(false);
      showToast(err.response?.data?.message || "Could not start the payment.", "error");
    }
  };

  if (loading) {
    return (
      <Shell>
        <PayCard className="tw-py-16 tw-text-center tw-text-content-muted">Loading…</PayCard>
      </Shell>
    );
  }

  if (loadError || !data) {
    return (
      <Shell>
        <PayCard className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-16 tw-text-center">
          <AlertTriangle size={28} className="tw-text-danger" />
          <h2 className="tw-mb-1 tw-mt-2 tw-text-xl">This link isn't available</h2>
          <p className="tw-m-0 tw-text-content-muted">{loadError}</p>
        </PayCard>
      </Shell>
    );
  }

  const done = paidInfo || data.paid;

  return (
    <Shell>
      <PayCard>
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="tw-mb-5 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-pb-4">
          <div>
            <div className="tw-text-xs tw-uppercase tw-tracking-caps tw-text-content-subtle">
              {data.platform.name} subscription
            </div>
            <h1 className="tw-mb-1.5 tw-mt-1 tw-text-2xl tw-font-semibold">{data.copy.headline}</h1>
            <div className="tw-text-sm tw-text-content-muted">{data.institute.name}</div>
          </div>
          <div className="tw-text-right tw-text-sm tw-text-content-muted">
            {data.institute.currentPlanName && (
              <div>Current plan: <strong className="tw-text-content">{data.institute.currentPlanName}</strong></div>
            )}
            {data.institute.subscriptionEnd && (
              <div>Expires {fmtDate(data.institute.subscriptionEnd)}</div>
            )}
            {data.institute.trialExpiresAt && data.institute.status === "trial" && (
              <div>Trial ends {fmtDate(data.institute.trialExpiresAt)}</div>
            )}
          </div>
        </div>

        {/* ── Terminal states ────────────────────────────────────────── */}
        {done && (
          <div className="tw-mb-5 tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-bg-success-soft tw-p-4 tw-text-success-fg">
            <CheckCircle size={20} className="tw-shrink-0" />
            <div>
              <strong>Payment received.</strong>{" "}
              {paidInfo?.subscription
                ? `${paidInfo.subscription.planName} is active until ${fmtDate(paidInfo.subscription.end)}.`
                : "This subscription has been paid."}
              {paidInfo?.invoiceNumber && (
                <div className="tw-mt-1 tw-text-sm tw-opacity-80">Invoice {paidInfo.invoiceNumber} — a receipt is on its way by email.</div>
              )}
            </div>
          </div>
        )}

        {!done && data.expired && (
          <div className="tw-mb-5 tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-bg-warning-soft tw-p-4 tw-text-warning-fg">
            <Clock size={20} className="tw-shrink-0" />
            <div>
              This payment link has expired. Log in to your LMS and open <strong>Billing</strong> to
              subscribe, or ask us for a fresh link.
            </div>
          </div>
        )}

        {!done && !data.expired && (
          <>
            <p className="tw-mb-5 tw-mt-0 tw-text-[0.95rem] tw-leading-relaxed tw-text-content-muted">
              {data.copy.body}
            </p>

            {/* ── Cycle toggle ───────────────────────────────────────── */}
            <div className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2">
              {CYCLES.filter((c) => data.plans.some((p) => p.cycles[c.key])).map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCycle(c.key)}
                  className={
                    "tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-px-4 tw-py-1.5 tw-text-sm tw-transition-colors "
                    + (cycle === c.key
                      ? "tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg"
                      : "tw-border-line tw-bg-surface-raised tw-text-content-muted hover:tw-bg-surface-hover")
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* ── Plan cards ─────────────────────────────────────────── */}
            <div className="tw-mb-6 tw-grid tw-gap-3 min-[700px]:tw-grid-cols-3">
              {data.plans.map((p) => {
                const pc = cycle ? p.cycles[cycle] : null;
                const selected = String(p._id) === String(planId);
                return (
                  <button
                    key={p._id}
                    type="button"
                    onClick={() => setPlanId(String(p._id))}
                    disabled={!pc}
                    className={
                      "tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-p-4 tw-text-left tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-50 "
                      + (selected
                        ? "tw-border-primary tw-bg-primary/[0.06]"
                        : "tw-border-line-soft tw-bg-surface-sunken hover:tw-bg-surface-hover")
                    }
                  >
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <strong className="tw-text-base">{p.name}</strong>
                      {p.isCurrent && (
                        <span className="tw-rounded-full tw-bg-info-soft tw-px-2 tw-py-0.5 tw-text-[0.68rem] tw-uppercase tw-tracking-caps tw-text-info-fg">
                          Current
                        </span>
                      )}
                    </div>
                    {pc ? (
                      <>
                        <div className="tw-mt-2 tw-text-xl tw-font-bold">
                          {money(pc.payable)}
                          <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-content-subtle">
                            /{cycle === "monthly" ? "mo" : cycle === "quarterly" ? "qtr" : "yr"}
                          </span>
                        </div>
                        {pc.discountAmount > 0 && (
                          <div className="tw-mt-0.5 tw-text-xs tw-text-success-fg">
                            <Sparkles size={11} className="tw-mr-1 tw-inline" />
                            <s className="tw-text-content-subtle">{money(pc.listPrice)}</s>{" "}
                            {pc.discountLabel || "discount applied"}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="tw-mt-2 tw-text-sm tw-text-content-subtle">Not offered {cycle && `(${cycle})`}</div>
                    )}
                    <div className="tw-mt-2 tw-text-xs tw-text-content-muted">
                      {p.maxStudents ? `${p.maxStudents} students` : "Unlimited students"}
                      {p.maxBatches ? ` · ${p.maxBatches} batches` : ""}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ── Amount + pay ───────────────────────────────────────── */}
            {priced && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-rounded-lg tw-bg-surface-sunken tw-p-4">
                <div>
                  <div className="tw-text-xs tw-text-content-subtle">You pay</div>
                  <div className="tw-text-3xl tw-font-bold tw-text-primary">{money(priced.total)}</div>
                  <div className="tw-mt-0.5 tw-text-xs tw-text-content-muted">
                    {money(priced.payable)}
                    {priced.taxAmount > 0 && ` + ${money(priced.taxAmount)} ${data.tax.label}`}
                    {" · "}{plan.name} · {cycle}
                  </div>
                </div>
                {/* The app's button, not a hand-rolled one — it brings its own
                    stylesheet, so it works on this unauthenticated page too. */}
                <AppButton
                  size="lg"
                  onClick={handlePay}
                  disabled={paying || !data.payable}
                  className="tw-gap-2"
                >
                  <CreditCard size={17} />
                  {paying ? "Opening…" : "Pay securely"}
                </AppButton>
              </div>
            )}

            <div className="tw-mt-4 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-content-subtle">
              <ShieldCheck size={14} />
              Payment is processed by Razorpay. A GST invoice is emailed to your administrators as soon
              as it clears — no login needed to pay.
            </div>
          </>
        )}
      </PayCard>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </Shell>
  );
};

export default PublicPlanRenewalPage;
